// Pinia Store
import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import { roundValue } from '../helpers/round'
import { omit as HelpersObjectOmit } from '../helpers/object'

export type PrintFiscalReceiptPayloadItem = {
  type?:
    | 'sale'
    | 'discount-amount'
    | 'surcharge-amount'
    | 'comment'
    | 'footer-comment' // 'sale' is default value
  text?: string // the name of the product
  quantity?: number // (optional) the quantity sold. When not specified, some fiscal printers might be able to omit the quantity completely, while others might print "1". For calculation purposes, omitting quantity means 1.
  unitPrice?: number // the unit price, not including any discounts/markups
  taxGroup?: number // the government regulated tax group. An integer from 1 to 8.
  department?: number // (optional) the department number. A positive integer.
  priceModifierValue?: number // (optional) modifies the total amount of the line according to the setting of "priceModifierType"
  priceModifierType?:
    | 'discount-percent'
    | 'discount-amount'
    | 'surcharge-percent'
    | 'surcharge-amount' // The item with type "discount-amount" and "surcharge-amount" can have the following fields set:
  amount?: number // the amount that will be substracted or added to the subtotal Warning: Check the value of "supportsSubTotalAmountModifiers" in your device info, to check whether your device supports subtotal modifiers by amount.
  // The item with type "comment" and "footer-comment" can have the following fields set:
  // "text" - the text of the comment
}
type PrintFiscalReceiptPayloadPayment = {
  amount: number
  paymentType:
    | 'cash'
    | 'check'
    | 'card'
    | 'coupons'
    | 'ext-coupons'
    | 'packaging'
    | 'internal-usage'
    | 'damage'
    | 'bank'
    | 'reserved1'
    | 'reserved2'
}
export type PrintFiscalReceiptPayload = {
  uniqueSaleNumber: string
  operator?: string
  operatorPassword?: string
  items: PrintFiscalReceiptPayloadItem[]
  payments: PrintFiscalReceiptPayloadPayment[]
}
export type PrintFiscalReversalReceiptPayload = {
  uniqueSaleNumber: string
  operator?: string
  operatorPassword?: string
  receiptNumber: number
  receiptDateTime: string
  fiscalMemorySerialNumber: string
  items: PrintFiscalReceiptPayloadItem[]
  payments: PrintFiscalReceiptPayloadPayment[]
  reason: 'operator-error'
}
type PrinterResultMessage = {
  type: string
  text: string
  code?: string // error code
}
export type PrintResult = {
  ok: boolean
  messages: PrinterResultMessage[]
  receiptNumber?: string
  receiptDateTime?: string
  receiptAmount?: number
  fiscalMemorySerialNumber?: string
  deviceDateTime?: string
}

// The minimum shape we need from `app.service('pi-fiscal')` on the cloud
// Feathers client. Apps wire this via setPiFiscalService(feathersClient.service('pi-fiscal')).
export type PiFiscalServiceShape = {
  find(params?: unknown): Promise<unknown>
  get(id: string, params?: unknown): Promise<unknown>
  create(
    data: { printerId: string; operation: string; [k: string]: unknown },
    params?: { query?: Record<string, unknown> },
  ): Promise<{ taskId?: string; ok?: boolean; messages?: PrinterResultMessage[]; [k: string]: unknown }>
  getStatus(id: string, params?: unknown): Promise<{
    ok: boolean
    messages: PrinterResultMessage[]
    [k: string]: unknown
  }>
  getTask(id: string, params?: unknown): Promise<{
    result?: PrintResult
    taskStatus?: string
    ok?: boolean
    messages?: PrinterResultMessage[]
  }>
}

const ErrorMessages = {
  printerId: {
    type: 'error',
    text: 'The fiscal printer is not setup in the settings',
  } as PrinterResultMessage,
  notWired: {
    type: 'error',
    text: 'Fiscal service is not wired — call setPiFiscalService at app boot',
  } as PrinterResultMessage,
}

export const getReceiptTotal = (printObject: PrintFiscalReceiptPayload) => {
  if (!printObject?.items?.length) return 0

  const total = printObject.items
    .filter((item) => item.type === 'sale')
    .reduce((acc, item) => {
      return roundValue(
        acc + roundValue((item.unitPrice || 0) * (item.quantity || 1)),
      )
    }, 0)

  return roundValue(total)
}

export const getReceiptPaymentsTotal = (
  printObject: PrintFiscalReceiptPayload,
) => {
  if (!printObject?.payments?.length) return 0

  const paymentsTotal = printObject.payments.reduce((acc, item) => {
    return roundValue(acc + (item.amount || 0))
  }, 0)

  return roundValue(paymentsTotal)
}

interface State {
  isFiscalDevMode: boolean
  isFiscalReceiptPreview: boolean
  printerId: null | string
  printerStatus: {
    ok: boolean
    status: 'idle' | 'ok' | 'not-set-up' | 'error' | 'loading'
    messages: PrinterResultMessage[]
  }
  service: PiFiscalServiceShape | null
}

const isFiscalDevMode = localStorage.getItem('isFiscalDevMode') === 'true'
const isFiscalReceiptPreview =
  localStorage.getItem('isFiscalReceiptPreview') === 'true'

export const useFiscalStore = defineStore('fiscal', {
  state: (): State => ({
    isFiscalDevMode: isFiscalDevMode,
    isFiscalReceiptPreview: isFiscalReceiptPreview,
    printerId: window.localStorage.getItem('printerId') || null,
    printerStatus: {
      ok: false,
      status: 'idle',
      messages: [],
    },
    service: null,
  }),
  getters: {},
  actions: {
    setPiFiscalService(service: PiFiscalServiceShape) {
      // Feathers client services are Proxies with non-configurable Symbol
      // keys (e.g. Symbol(@feathersjs/service)) that the reactive wrapper
      // Pinia would otherwise wrap can't forward — using one through the
      // reactive proxy throws on the first method call. markRaw opts the
      // service out of reactivity; we only ever call methods on it anyway.
      this.$state.service = markRaw(service) as PiFiscalServiceShape
    },

    setPrinterId(printerId: string | null) {
      if (printerId) {
        window.localStorage.setItem('printerId', printerId)
        this.$state.printerId = printerId
      }
    },

    resetPrinterId() {
      window.localStorage.removeItem('printerId')
      this.$state.printerId = null
    },

    async getPrintersList() {
      if (!this.$state.service) {
        console.error(ErrorMessages.notWired.text)

        return []
      }
      try {
        return await this.$state.service.find()
      } catch (error) {
        console.error('getPrintersList ~ error:', error)

        return []
      }
    },

    async updatePrinterStatus() {
      if (!this.$state.printerId) {
        this.$state.printerStatus = {
          ok: false,
          status: 'not-set-up',
          messages: [ErrorMessages.printerId],
        }

        return
      }

      // Dev mode short-circuit — print methods are stubbed via
      // useDevCheckoutStubs, so the real status round-trip would either
      // hit a non-existent printer ("12345") or block the bridge with a
      // 30s timeout. Skip and report a synthetic OK so the UI unblocks.
      if (this.$state.isFiscalDevMode) {
        this.$state.printerStatus = { ok: true, status: 'ok', messages: [] }

        return
      }

      if (!this.$state.service) {
        this.$state.printerStatus = {
          ok: false,
          status: 'not-set-up',
          messages: [ErrorMessages.notWired],
        }

        return
      }

      this.$state.printerStatus.status = 'loading'

      try {
        const printerStatus = await this.$state.service.getStatus(this.$state.printerId)
        this.$state.printerStatus = {
          ...printerStatus,
          status: printerStatus?.ok ? 'ok' : 'error',
        }
      } catch (error: any) {
        console.error('updatePrinterStatus ~ error:', error)

        this.$state.printerStatus = {
          ok: false,
          messages: [{ type: 'error', text: error?.name || 'unknown' }],
          status: 'error',
        }
      }
    },

    async printDuplicate() {
      if (!this.$state.service || !this.$state.printerId) {
        console.error(this.$state.printerId ? ErrorMessages.notWired.text : ErrorMessages.printerId.text)

        return
      }
      if (this.$state.isFiscalDevMode) {
        console.log('DEV_MODE printing duplicate...')

        return
      }

      return this.$state.service.create({
        printerId: this.$state.printerId,
        operation: 'duplicate',
      })
    },

    async printXReport() {
      if (!this.$state.service || !this.$state.printerId) {
        console.error(this.$state.printerId ? ErrorMessages.notWired.text : ErrorMessages.printerId.text)

        return
      }
      if (this.$state.isFiscalDevMode) {
        console.log('DEV_MODE printing X Report...')

        return
      }

      return this.$state.service.create({
        printerId: this.$state.printerId,
        operation: 'xreport',
      })
    },

    async printZReport() {
      if (!this.$state.service || !this.$state.printerId) {
        console.error(this.$state.printerId ? ErrorMessages.notWired.text : ErrorMessages.printerId.text)

        return
      }
      if (this.$state.isFiscalDevMode) {
        console.log('DEV_MODE printing Z Report...')

        return
      }

      return this.$state.service.create({
        printerId: this.$state.printerId,
        operation: 'zreport',
      })
    },

    // Fiscal-memory period report by date range (ISL/ICP cmd 0x55).
    // Prints on the fiscal roll: turnover by tax group + VAT for the
    // range — the document the accountant needs, computed by the device
    // from fiscal memory (matches NRA). `detailed` prints a per-Z-block
    // breakdown, otherwise a period summary; `withPayments` adds a
    // per-payment-type split (ISL50xx+). Only Z-closed days appear.
    // rawRequest = "55" + <2-char type> + fromDDMMYYYY + toDDMMYYYY,
    // where the type's first digit is 0/1/2/3 and the second is always 1.
    async printFmDateReport(
      from: Date,
      to: Date,
      opts: { detailed?: boolean; withPayments?: boolean } = {},
    ) {
      if (!this.$state.service || !this.$state.printerId) {
        console.error(this.$state.printerId ? ErrorMessages.notWired.text : ErrorMessages.printerId.text)

        return
      }
      if (this.$state.isFiscalDevMode) {
        console.log('DEV_MODE printing FM date report...', { from, to, opts })

        return
      }

      const fmt = (d: Date) =>
        `${String(d.getDate()).padStart(2, '0')}` +
        `${String(d.getMonth() + 1).padStart(2, '0')}` +
        `${d.getFullYear()}`
      const typeDigit = (opts.detailed ? 0 : 1) + (opts.withPayments ? 2 : 0)
      const rawRequest = `55${typeDigit}1${fmt(from)}${fmt(to)}`

      return this.$state.service.create({
        printerId: this.$state.printerId,
        operation: 'rawrequest',
        rawRequest,
      })
    },

    async printReceipt(
      printObject: PrintFiscalReceiptPayload,
      transactionId: string,
    ): Promise<{ taskId?: string; ok?: boolean; messages?: PrinterResultMessage[] }> {
      if (!this.$state.service) {
        return { ok: false, messages: [ErrorMessages.notWired] }
      }
      if (!this.$state.printerId) {
        return { ok: false, messages: [ErrorMessages.printerId] }
      }

      return this.$state.service.create(
        {
          printerId: this.$state.printerId,
          operation: 'receipt',
          ...printObject,
        },
        { query: { asyncTimeout: 0, taskId: transactionId } },
      )
    },

    async getTask(transactionId: string): Promise<{
      result?: PrintResult
      taskStatus?: string
      ok?: boolean
      messages?: PrinterResultMessage[]
    }> {
      if (!this.$state.service) {
        return { ok: false, messages: [ErrorMessages.notWired] }
      }
      if (!this.$state.printerId) {
        return { ok: false, messages: [ErrorMessages.printerId] }
      }

      return this.$state.service.getTask(transactionId)
    },

    async printReversalReceipt(
      printObject: PrintFiscalReversalReceiptPayload,
    ): Promise<PrintResult> {
      if (!this.$state.service) {
        return { ok: false, messages: [ErrorMessages.notWired] }
      }
      if (!this.$state.printerId) {
        return { ok: false, messages: [ErrorMessages.printerId] }
      }

      const { payments: _payments, ...body } = HelpersObjectOmit(printObject, 'payments') as any

      return this.$state.service.create({
        printerId: this.$state.printerId,
        operation: 'reversalreceipt',
        ...body,
      }) as Promise<PrintResult>
    },
  },
})
